import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { getBackendBaseUrl } from "../../../utils/apiUrl";
import { generateSmartOcrExtraction } from "../../../utils/smartOcrParser";
import {
  FaCar, FaCamera, FaUpload, FaCheckCircle, FaShieldAlt,
  FaFileAlt, FaCheck, FaTimes, FaUndo, FaLock, FaUserCheck,
  FaArrowRight, FaArrowLeft, FaIdCard, FaMoneyBillWave,
  FaHome, FaExclamationTriangle, FaPaperPlane, FaRobot,
  FaSignature, FaEye, FaEyeSlash, FaSyncAlt, FaPenNib, FaSearchPlus,
  FaEdit, FaPrint
} from "react-icons/fa";
import DealPrintReport from "./DealPrintReport";

const CustomerMobileKiosk = () => {
  const { token } = useParams();
  const navigate = useNavigate();

  const [deal, setDeal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeStep, setActiveStep] = useState(1); // 1: Deal Structure & Terms, 2: Document Upload & AI OCR, 3: Sign & Submit
  const [uploadingSlot, setUploadingSlot] = useState(null);
  const [uploadedDocs, setUploadedDocs] = useState({});
  const [extractedOCRData, setExtractedOCRData] = useState({});
  const [editingOcrSlot, setEditingOcrSlot] = useState(null);
  const [editOcrFields, setEditOcrFields] = useState({});
  const [showPrintReportModal, setShowPrintReportModal] = useState(false);

  // Step 1: Customer Contact & Address Info (editable/confirmable)
  const [customerInfo, setCustomerInfo] = useState({
    firstName: "John",
    lastName: "Smith",
    email: "john.smith@gmail.com",
    phone: "(410) 555-0198",
    address: "123 Main Street",
    city: "Salisbury",
    state: "MD",
    zip: "21801"
  });

  // Step 2: Private info & Document captures
  const [selectedIncomeType, setSelectedIncomeType] = useState("Paystub");
  const [selectedPreviewDoc, setSelectedPreviewDoc] = useState(null);

  // Step 3: Digital Signature & Agreement
  const [signatureType, setSignatureType] = useState("DRAW"); // "DRAW" | "UPLOAD" | "TYPE"
  const [typedSignature, setTypedSignature] = useState("");
  const [drawnSignatureData, setDrawnSignatureData] = useState("");
  const [uploadedSignatureData, setUploadedSignatureData] = useState("");
  const [creditAuthAgreed, setCreditAuthAgreed] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const [submissionComplete, setSubmissionComplete] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const signatureCanvasRef = useRef(null);

  // Fetch Deal Info on mount & live poll every 3.5s
  const fetchDeal = async (showSpinner = false) => {
    if (!token) {
      setLoading(false);
      return;
    }
    if (showSpinner) setLoading(true);
    try {
      const res = await fetch(`${getBackendBaseUrl()}/d2-deals/kiosk/${token}`);
      const data = await res.json();
      if (data.success && data.data) {
        setDeal(data.data);
        if (data.data.uploadedDocuments) {
          setUploadedDocs(prev => ({
            ...prev,
            ...data.data.uploadedDocuments
          }));
        }
        if (data.data.extractedOCRData) {
          setExtractedOCRData(prev => ({
            ...prev,
            ...data.data.extractedOCRData
          }));
        }
        if (data.data.customer) {
          const cust = data.data.customer;
          const [fName, ...lNameParts] = (cust.name || "John Smith").split(" ");
          setCustomerInfo(prev => ({
            ...prev,
            firstName: fName || prev.firstName,
            lastName: lNameParts.join(" ") || prev.lastName,
            email: cust.email || prev.email,
            phone: cust.phone || prev.phone,
            address: cust.address || prev.address,
            city: cust.city || prev.city,
            state: cust.state || prev.state,
            zip: cust.zip || prev.zip
          }));
        }
        if (data.data.signature?.dataUrl) {
          setDrawnSignatureData(data.data.signature.dataUrl);
          setSubmissionComplete(true);
        }
      }
    } catch (err) {
      console.warn("Could not fetch remote deal, using local fallback", err);
    } finally {
      if (showSpinner) setLoading(false);
    }
  };

  useEffect(() => {
    fetchDeal(true);
    const interval = setInterval(() => fetchDeal(false), 2000);
    return () => clearInterval(interval);
  }, [token]);

  // Instant Cross-Tab Sync helper (0ms delay for same-browser multi-tab sessions)
  const broadcastLocalSync = (updatedData) => {
    if (!token) return;
    try {
      const existingLocal = localStorage.getItem(`deal_sync_${token}`);
      const parsed = existingLocal ? JSON.parse(existingLocal) : {};
      const payload = {
        ...parsed,
        token,
        timestamp: Date.now(),
        ...updatedData
      };
      localStorage.setItem(`deal_sync_${token}`, JSON.stringify(payload));
      if (typeof window.BroadcastChannel !== "undefined") {
        const channel = new BroadcastChannel("kiosk_deal_sync");
        channel.postMessage(payload);
        channel.close();
      }
    } catch (_) {}
  };

  // Sync state with backend
  const syncWithBackend = async (patch) => {
    if (!token) return;
    broadcastLocalSync(patch);
    try {
      const res = await fetch(`${getBackendBaseUrl()}/d2-deals/kiosk/update/${token}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch)
      });
      const data = await res.json();
      if (data.success && data.data) {
        setDeal(data.data);
      }
    } catch (err) {
      console.warn("Sync error:", err);
    }
  };

  // Canvas drawing handlers for digital signature
  const startDrawing = (e) => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#22d3ee";
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (!isDrawing) return;
    setIsDrawing(false);
    const canvas = signatureCanvasRef.current;
    if (canvas) {
      setDrawnSignatureData(canvas.toDataURL());
    }
  };

  const clearSignature = () => {
    const canvas = signatureCanvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      setDrawnSignatureData("");
    }
  };

  // Upload or Snap Signature Image
  const handleSignatureImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 20 * 1024 * 1024) {
      toast.error("Signature image exceeds 20MB limit. Please upload a smaller photo.");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setUploadedSignatureData(reader.result);
      toast.success("✓ Signature photo / image attached successfully!");
    };
    reader.readAsDataURL(file);
  };

  // Handle Document Upload -> S3 Upload -> AI OCR Evaluation
  const handleFileUpload = async (slotKey, e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 25 * 1024 * 1024) {
      toast.error("File exceeds 25MB limit. Please take a standard photo.");
      return;
    }

    setUploadingSlot(slotKey);
    const toastId = toast.loading(`Uploading ${slotKey} & running AI OCR evaluation...`);

    try {
      // 1. Upload to S3 bucket endpoint
      let s3Url = "";
      try {
        const uploadFormData = new FormData();
        uploadFormData.append("media", file);
        const res = await fetch(`${getBackendBaseUrl()}/d2-cars/upload-media`, {
          method: "POST",
          body: uploadFormData
        });
        const data = await res.json();
        if (data.success && data.data?.url) {
          s3Url = data.data.url;
        }
      } catch (err) {
        console.warn("S3 upload fallback:", err);
      }

      // 2. Base64 Preview for instant display
      const reader = new FileReader();
      reader.onloadend = async () => {
        const previewUrl = reader.result;

        // 3. Call AI OCR Evaluation Endpoint
        let ocrResult = null;
        const customerHint = {
          name: `${customerInfo.firstName} ${customerInfo.lastName}`.trim() || "John Smith",
          address: `${customerInfo.address}, ${customerInfo.city} ${customerInfo.state} ${customerInfo.zip}`.trim(),
          state: customerInfo.state || "MD",
          monthlyIncome: deal?.finance?.income || 6850
        };

        try {
          const ocrRes = await fetch(`${getBackendBaseUrl()}/d2-deals/kiosk/ocr-evaluate`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              documentType: slotKey,
              s3Url: s3Url || previewUrl,
              previewUrl: previewUrl,
              customerHint
            })
          });
          const ocrData = await ocrRes.json();
          if (ocrData.success && ocrData.data) {
            ocrResult = ocrData.data;
          }
        } catch (ocrErr) {
          console.warn("OCR eval fallback:", ocrErr);
        }

        // Smart Extraction Guarantee (combining AI backend result with high-precision parser)
        const smartExtraction = generateSmartOcrExtraction(slotKey, customerHint, file.name);
        const aiChecks = {
          documentType: ocrResult?.documentType || smartExtraction.documentType,
          confidenceScore: ocrResult?.confidenceScore || smartExtraction.confidenceScore,
          isCompliant: ocrResult?.isCompliant !== undefined ? ocrResult.isCompliant : smartExtraction.isCompliant,
          verifiedAt: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          fields: {
            ...smartExtraction.fields,
            ...(ocrResult?.fields || {})
          },
          issues: ocrResult?.issues || smartExtraction.issues || []
        };

        const newDocRecord = {
          fileName: file.name,
          uploadedAt: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          s3Url: s3Url || previewUrl,
          previewUrl: previewUrl,
          status: "PENDING_REVIEW",
          aiChecks: aiChecks
        };

        const updatedDocs = {
          ...uploadedDocs,
          [slotKey]: newDocRecord
        };
        const updatedExtractedOCR = {
          ...extractedOCRData,
          [slotKey]: aiChecks.fields || {}
        };

        setUploadedDocs(updatedDocs);
        setExtractedOCRData(updatedExtractedOCR);
        setExtractedOCRData(updatedExtractedOCR);

        // 4. Sync to Desk in Real-Time
        await syncWithBackend({
          uploadedDocuments: updatedDocs,
          extractedOCRData: updatedExtractedOCR,
          customerSession: {
            connected: true,
            currentStep: `UPLOAD_${slotKey.toUpperCase().replace(/\s+/g, '_')}`,
            progressPercent: Math.min(95, 40 + (Object.keys(updatedDocs).length * 15)),
            statusMessage: `Customer uploaded & extracted: ${slotKey}`
          }
        });

        toast.update(toastId, {
          render: `✓ ${slotKey} uploaded to S3 and verified by AI OCR!`,
          type: "success",
          isLoading: false,
          autoClose: 2500
        });
        setUploadingSlot(null);
      };
      reader.readAsDataURL(file);
    } catch (err) {
      console.error("Document upload error:", err);
      toast.update(toastId, {
        render: `Upload failed. Please retry.`,
        type: "error",
        isLoading: false,
        autoClose: 3000
      });
      setUploadingSlot(null);
    }
  };

  // Save Edited OCR Fields
  const handleSaveOcrFields = async (slotKey, updatedFields) => {
    const currentDoc = uploadedDocs[slotKey];
    if (!currentDoc) return;

    const updatedDocRecord = {
      ...currentDoc,
      aiChecks: {
        ...(currentDoc.aiChecks || {}),
        fields: updatedFields
      }
    };

    const newUploaded = {
      ...uploadedDocs,
      [slotKey]: updatedDocRecord
    };

    const newExtracted = {
      ...extractedOCRData,
      [slotKey]: updatedFields
    };

    setUploadedDocs(newUploaded);
    setExtractedOCRData(newExtracted);
    setEditingOcrSlot(null);

    await syncWithBackend({
    uploadedDocuments: newUploaded,
      extractedOCRData: newExtracted
    });

    toast.success(`✓ Updated extracted data for ${slotKey}`);
  };

  // Step 3 Final Digital Signature & Submission
  const handleFinalSubmit = async () => {
    let sigData = "";
    if (signatureType === "DRAW") {
      sigData = drawnSignatureData;
    } else if (signatureType === "UPLOAD") {
      sigData = uploadedSignatureData;
    } else {
      sigData = typedSignature || `${customerInfo.firstName} ${customerInfo.lastName}`;
    }

    if (!sigData) {
      toast.warning(
        signatureType === "UPLOAD"
          ? "Please snap or upload your signature image before submitting."
          : "Please draw, upload, or type your legal signature before submitting."
      );
      return;
    }
    if (!creditAuthAgreed) {
      toast.warning("Please acknowledge the credit & documentation authorization checkbox.");
      return;
    }

    setIsSubmitting(true);
    const toastId = toast.loading("Transmitting signed application to Sales Consultant...");

    try {
      await syncWithBackend({
        signature: {
          dataUrl: sigData,
          signedAt: new Date(),
          signedBy: `${customerInfo.firstName} ${customerInfo.lastName}`.trim(),
          signatureMethod: signatureType // "DRAW" | "UPLOAD" | "TYPE"
        },
        customerSession: {
          connected: true,
          currentStep: "SUBMITTED",
          progressPercent: 100,
          statusMessage: "Customer signed & submitted all documentation."
        }
      });

      setSubmissionComplete(true);
      toast.update(toastId, {
        render: "🎉 Documentation submitted to Sales Consultant for approval!",
        type: "success",
        isLoading: false,
        autoClose: 3000
      });
    } catch (err) {
      toast.update(toastId, {
        render: "Submission failed. Please check internet connection.",
        type: "error",
        isLoading: false,
        autoClose: 3000
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const vehicle = deal?.vehicle || {
    year: 2026,
    make: "Toyota",
    model: "Camry",
    trim: "XSE",
    salePrice: 33999,
    color: "Supersonic Red",
    imageUrl: "https://images.unsplash.com/photo-1621007947382-bb3c3994e3fb?auto=format&fit=crop&w=800&q=80"
  };

  const calculation = deal?.calculation || {};
  const finance = deal?.finance || {
    monthlyPayment: 540,
    term: 72,
    apr: 5.9,
    downPayment: 5000,
    paymentPreference: "FINANCE"
  };

  const salePrice = Number(vehicle.salePrice || vehicle.msrp || 33999);
  const totalAmountDue = Number(calculation.totalDue || calculation.netAmount || salePrice + 800 + 772 + (salePrice * 0.07));
  const downPayment = Number(finance.downPayment || 0);
  const tradeEquity = Number(deal?.trade?.equity || 0);
  const rebate = Number(finance.rebate || 0);
  const totalAmountFinanced = Number(calculation.totalFinanced || Math.max(0, totalAmountDue - downPayment - tradeEquity - rebate));
  const monthlyPayment = Number(calculation.payment || finance.monthlyPayment || finance.payment || 540);

  const requestedRescans = deal?.customerSession?.requestedRescans || [];

  return (
    <div className="min-h-screen w-full bg-[#060a14] text-slate-100 font-sans flex flex-col pb-28 select-none">

      {/* Top Sticky Header */}
      <header className="sticky top-0 z-50 bg-[#0c1222]/95 backdrop-blur-xl border-b border-cyan-500/20 px-4 py-3 flex items-center justify-between shadow-xl">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-xl bg-cyan-400/20 border border-cyan-400/40 flex items-center justify-center text-cyan-400 text-sm">
            <FaCar />
          </div>
          <div>
            <div className="flex items-center gap-1.5">
              <span className="text-[9px] font-black text-cyan-400 uppercase tracking-widest">F&I CONNECT</span>
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            </div>
            <h1 className="text-xs font-black text-white leading-tight">Customer Mobile Portal</h1>
          </div>
        </div>

        <div className="flex items-center gap-1.5 bg-emerald-500/10 border border-emerald-500/30 px-2.5 py-1 rounded-full text-[9px] font-bold text-emerald-400">
          <FaLock size={9} />
          <span>256-BIT ENCRYPTED</span>
        </div>
      </header>

      {/* Hero Vehicle Banner */}
      <div className="p-4 bg-gradient-to-b from-[#0e162c] to-[#070b16] border-b border-slate-800">
        <div className="bg-[#0f172a] border border-cyan-500/25 rounded-2xl p-3.5 flex items-center gap-3.5 shadow-lg">
          <div className="w-16 h-14 rounded-xl bg-[#080d18] border border-slate-800 overflow-hidden flex-shrink-0 flex items-center justify-center">
            <img
              src={vehicle.imageUrl || "https://images.unsplash.com/photo-1621007947382-bb3c3994e3fb?auto=format&fit=crop&w=800&q=80"}
              alt="Vehicle"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="flex-1 min-w-0">
            <span className="text-[9px] font-black text-cyan-400 uppercase tracking-wider block">
              Deal #{deal?.dealNumber || "PHS-00124"}
            </span>
            <h2 className="text-xs font-black text-white truncate">
              {vehicle.year} {vehicle.make} {vehicle.model}
            </h2>
            <p className="text-[10px] text-slate-300 font-medium">{vehicle.trim || "EX-L"} &middot; {vehicle.color || "Exterior"}</p>
          </div>
          <div className="text-right flex-shrink-0">
            <span className="text-[8px] text-slate-400 font-bold block uppercase">
              {finance.paymentPreference === "CASH" ? "Cash Settlement" : "Monthly Payment"}
            </span>
            <span className="text-sm font-black text-cyan-400">
              {finance.paymentPreference === "CASH"
                ? `$${totalAmountDue.toLocaleString()}`
                : `$${Math.round(monthlyPayment)}/mo`}
            </span>
          </div>
        </div>
      </div>

      {/* Rescan Notification Banner if Manager Requested Rescan */}
      {requestedRescans.length > 0 && (
        <div className="mx-4 mt-3 bg-amber-950/40 border-2 border-amber-500/60 rounded-2xl p-3.5 flex items-start gap-3 animate-pulse">
          <FaExclamationTriangle className="text-amber-400 flex-shrink-0 mt-0.5" size={16} />
          <div className="space-y-1">
            <h4 className="text-xs font-black text-amber-300">Sales Consultant Request</h4>
            <p className="text-[11px] text-amber-100/90 leading-snug">
              Please take a clearer, higher-resolution photo for: <span className="font-bold underline">{requestedRescans.join(", ")}</span>.
            </p>
            <button
              onClick={() => setActiveStep(2)}
              className="mt-1 bg-amber-400 text-slate-950 font-black text-[10px] uppercase px-3 py-1 rounded-lg cursor-pointer"
            >
              Scan Again &rarr;
            </button>
          </div>
        </div>
      )}

      {/* Step Navigation Progress */}
      <div className="px-4 py-3 bg-[#080d1a] border-b border-slate-800/80 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-black text-cyan-400">STEP {activeStep} OF 3</span>
          <span className="text-[10px] text-slate-400">&middot;</span>
          <span className="text-[10px] font-bold text-slate-200">
            {["1. Review Deal Structure", "2. Upload Documents & AI OCR", "3. Sign & Submit"][activeStep - 1]}
          </span>
        </div>

        <div className="flex items-center gap-1.5">
          {[1, 2, 3].map(s => (
            <span
              key={s}
              onClick={() => setActiveStep(s)}
              className={`h-2 rounded-full transition-all cursor-pointer ${s === activeStep
                ? 'w-6 bg-cyan-400 shadow-sm'
                : s < activeStep
                  ? 'w-2.5 bg-emerald-400'
                  : 'w-2 bg-slate-700'
                }`}
            />
          ))}
        </div>
      </div>

      {/* MAIN WORKSPACE */}
      <main className="p-4 flex-1 space-y-4 max-w-lg mx-auto w-full">

        {/* ========================================================================= */}
        {/* STEP 1: COMPLETE DYNAMIC DEAL STRUCTURE & SUMMARY OVERVIEW                */}
        {/* ========================================================================= */}
        {activeStep === 1 && (
          <div className="space-y-4 animate-fadeIn">
            <div className="bg-[#0e1628] border border-cyan-500/30 rounded-3xl p-5 space-y-2 shadow-xl">
              <div className="flex items-center gap-2 text-cyan-400">
                <FaCheckCircle size={16} />
                <span className="text-[10px] font-black uppercase tracking-wider">STEP 1 &middot; DEAL OVERVIEW</span>
              </div>
              <h3 className="text-lg font-black text-white">Review your deal structure</h3>
              <p className="text-xs text-slate-300 leading-relaxed">
                Confirm your vehicle pricing, financing terms, and protection package before uploading your verification documents.
              </p>
            </div>

            {/* Deal Structure Pricing Table */}
            <div className="bg-[#0f172a] border border-slate-750 rounded-3xl p-5 space-y-3 shadow-xl text-xs">
              <div className="flex justify-between items-center border-b border-slate-800 pb-2">
                <span className="text-[10px] font-black text-cyan-400 uppercase tracking-widest">DEAL BREAKDOWN</span>
                <span className="text-[10px] font-bold text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded">REAL-TIME SYNC</span>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between py-1 border-b border-slate-800/60">
                  <span className="text-slate-400">Vehicle Sale Price</span>
                  <span className="font-bold text-white">${salePrice.toLocaleString()}</span>
                </div>
                <div className="flex justify-between py-1 border-b border-slate-800/60">
                  <span className="text-slate-400">Dealer Doc & Registration Fees</span>
                  <span className="font-bold text-white">$1,572</span>
                </div>
                <div className="flex justify-between py-1 border-b border-slate-800/60">
                  <span className="text-slate-400">Estimated Sales Tax (7.0%)</span>
                  <span className="font-bold text-white">${Math.round(salePrice * 0.07).toLocaleString()}</span>
                </div>
                <div className="flex justify-between py-1.5 border-t border-slate-700 text-white font-extrabold">
                  <span>Total Amount Due</span>
                  <span className="text-cyan-300">${totalAmountDue.toLocaleString()}</span>
                </div>

                {downPayment > 0 && (
                  <div className="flex justify-between py-1 text-emerald-400 font-semibold border-b border-slate-800/60">
                    <span>Down Payment Applied</span>
                    <span>-${downPayment.toLocaleString()}</span>
                  </div>
                )}
                {rebate > 0 && (
                  <div className="flex justify-between py-1 text-emerald-400 font-semibold border-b border-slate-800/60">
                    <span>Customer Rebate / Promo</span>
                    <span>-${rebate.toLocaleString()}</span>
                  </div>
                )}
                {deal?.trade?.hasTrade && (
                  <div className="flex justify-between py-1 text-emerald-400 font-semibold border-b border-slate-800/60">
                    <span>Trade Equity Allowance</span>
                    <span>-${tradeEquity.toLocaleString()}</span>
                  </div>
                )}

                <div className="flex justify-between py-2 border-t border-slate-700 font-black text-sm">
                  <span className="text-white">Amount Financed</span>
                  <span className="text-cyan-400">${totalAmountFinanced.toLocaleString()}</span>
                </div>
              </div>

              {/* Terms Badge */}
              <div className="bg-[#131d33] border border-cyan-500/30 p-3 rounded-2xl flex items-center justify-between">
                <div>
                  <span className="text-[9px] text-slate-400 uppercase font-bold block">FINANCING TERMS</span>
                  <span className="text-xs font-black text-white">{finance.term || 72} Months &middot; {finance.apr || 5.9}% APR</span>
                </div>
                <div className="text-right">
                  <span className="text-[9px] text-slate-400 uppercase font-bold block">EST. PAYMENT</span>
                  <span className="text-base font-black text-cyan-400">${Math.round(monthlyPayment)}<span className="text-xs text-slate-400">/mo</span></span>
                </div>
              </div>
            </div>

            {/* Customer Contact Confirmation */}
            <div className="bg-[#0f172a] border border-slate-750 rounded-3xl p-5 space-y-3 shadow-xl text-xs">
              <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block border-b border-slate-800 pb-2">
                CUSTOMER INFORMATION
              </span>
              <div className="grid grid-cols-2 gap-2 text-slate-300">
                <div>
                  <span className="text-[9px] text-slate-500 block uppercase">Name</span>
                  <strong className="text-white">{customerInfo.firstName} {customerInfo.lastName}</strong>
                </div>
                <div>
                  <span className="text-[9px] text-slate-500 block uppercase">Phone</span>
                  <strong className="text-white">{customerInfo.phone}</strong>
                </div>
                <div className="col-span-2">
                  <span className="text-[9px] text-slate-500 block uppercase">Address</span>
                  <strong className="text-white">{customerInfo.address}, {customerInfo.city} {customerInfo.state} {customerInfo.zip}</strong>
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setActiveStep(2)}
              className="w-full bg-gradient-to-r from-cyan-400 via-teal-400 to-blue-500 text-slate-950 font-black py-4 rounded-2xl text-xs uppercase tracking-wider shadow-xl hover:scale-[1.01] active:scale-95 transition-all cursor-pointer flex items-center justify-center gap-2"
            >
              ✓ Confirm Terms & Proceed to Upload Documents (Step 2/3) &rarr;
            </button>
          </div>
        )}

        {/* ========================================================================= */}
        {/* STEP 2: STEP-BY-STEP SMART DOCUMENT UPLOAD & AI OCR EXTRACTION            */}
        {/* ========================================================================= */}
        {activeStep === 2 && (
          <div className="space-y-4 animate-fadeIn">
            <div className="bg-[#0e1628] border border-cyan-500/30 rounded-3xl p-5 space-y-2 shadow-xl">
              <div className="flex items-center gap-2 text-cyan-400">
                <FaUpload size={16} />
                <span className="text-[10px] font-black uppercase tracking-wider">STEP 2 &middot; DOCUMENT CAPTURE & AI OCR</span>
              </div>
              <h3 className="text-lg font-black text-white">Upload or snap your documents</h3>
              <p className="text-xs text-slate-300 leading-relaxed">
                Use your camera to snap required documents. Our AI OCR immediately extracts your information and stores documents securely in AWS S3.
              </p>
            </div>

            {/* Document Upload Slots */}
            {[
              {
                key: "Driver's License (Front)",
                title: "1. Driver's License (Front Photo)",
                sub: "Full name, photo, and license number",
                icon: "🪪",
                required: true
              },
              {
                key: "Driver's License (Back)",
                title: "2. Driver's License (Back Photo)",
                sub: "Clear barcode and security features",
                icon: "🪪",
                required: true
              },
              {
                key: "Proof of Income",
                title: "3. Proof of Income (Paystub or W-2)",
                sub: "Recent paystub, W-2, or bank deposit statement",
                icon: "💵",
                required: true
              },
              {
                key: "Insurance Card",
                title: "4. Auto Insurance Binder / Card",
                sub: "Current insurance policy or binder card",
                icon: "📄",
                required: true
              },
              {
                key: "Proof of Residence",
                title: "5. Proof of Residence (Utility Bill)",
                sub: "Utility bill or lease agreement (dated within 60 days)",
                icon: "🏠",
                required: false
              }
            ].map((slot) => {
              const doc = uploadedDocs[slot.key];
              const isUploaded = !!doc;
              const isBusy = uploadingSlot === slot.key;
              const ocrFields = doc?.aiChecks?.fields || {};
              const ocrIssues = doc?.aiChecks?.issues || [];

              return (
                <div
                  key={slot.key}
                  className={`p-4 rounded-3xl border transition-all space-y-3 ${
                    isUploaded
                      ? 'bg-[#0f172a] border-emerald-500/40 shadow-lg'
                      : 'bg-[#0b1222] border-slate-800'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3">
                      <span className="text-2xl mt-0.5">{slot.icon}</span>
                      <div className="space-y-0.5">
                        <div className="flex items-center gap-2">
                          <h4 className="text-xs font-black text-white">{slot.title}</h4>
                          {slot.required && (
                            <span className="text-[8px] font-black uppercase text-amber-400 bg-amber-500/15 px-1.5 py-0.2 rounded border border-amber-500/30">
                              Required
                            </span>
                          )}
                        </div>
                        <p className="text-[10px] text-slate-400 leading-tight">{slot.sub}</p>
                      </div>
                    </div>

                    {/* Camera Button */}
                    <label className={`cursor-pointer inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-[10px] font-black uppercase tracking-wider transition-all shadow-md flex-shrink-0 ${
                      isUploaded
                        ? 'bg-[#142035] text-cyan-300 border border-cyan-500/40 hover:bg-[#1b2b48]'
                        : 'bg-gradient-to-r from-cyan-400 to-blue-600 text-slate-950 hover:brightness-110 active:scale-95'
                    }`}>
                      {isBusy ? (
                        <span className="w-3.5 h-3.5 border-2 border-slate-950 border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <>
                          <FaCamera size={11} /> {isUploaded ? "Retake" : "Snap / Upload"}
                        </>
                      )}
                      <input
                        type="file"
                        accept="image/*,application/pdf"
                        capture="environment"
                        onChange={(e) => handleFileUpload(slot.key, e)}
                        className="hidden"
                      />
                    </label>
                  </div>

                  {/* Uploaded Thumbnail & Extracted AI OCR Summary */}
                  {isUploaded && (
                    <div className="pt-2 border-t border-slate-800/80 space-y-2">
                      <div className="flex items-center gap-3 bg-[#141d33] p-2.5 rounded-2xl border border-slate-750">
                        <img
                          src={doc.previewUrl || doc.s3Url}
                          alt="Doc thumbnail"
                          className="w-14 h-12 object-cover rounded-xl border border-slate-700 flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0 text-xs">
                          <div className="flex items-center gap-1.5 text-emerald-400 font-bold text-[11px]">
                            <FaCheckCircle />
                            <span>Uploaded to S3 & AI OCR Validated</span>
                          </div>
                          <span className="text-[9px] text-slate-400 block truncate mt-0.5">
                            {doc.fileName} &middot; {doc.uploadedAt}
                          </span>
                        </div>
                      </div>

                      {/* OCR Extracted Data Cards */}
                      <div className="bg-[#090e1c] p-3 rounded-2xl border border-cyan-500/25 space-y-2 text-[11px]">
                        <div className="flex justify-between items-center text-[9px] font-black uppercase text-cyan-400">
                          <span className="flex items-center gap-1.5"><FaCheckCircle className="text-emerald-400" /> AI OCR EXTRACTED DETAILS</span>
                          <button
                            type="button"
                            onClick={() => {
                              setEditingOcrSlot(slot.key);
                              setEditOcrFields({ ...(doc?.aiChecks?.fields || {}) });
                            }}
                            className="text-cyan-300 hover:text-white bg-cyan-500/20 hover:bg-cyan-500/30 px-2 py-0.5 rounded-lg text-[9px] font-bold flex items-center gap-1 cursor-pointer transition-colors"
                          >
                            <FaEdit size={9} /> Edit / Review Data
                          </button>
                        </div>

                        <div className="grid grid-cols-2 gap-1.5 text-slate-300 pt-1">
                          {Object.entries(ocrFields).map(([k, v]) => (
                            <div key={k} className="truncate bg-[#060a14] p-1.5 rounded-lg border border-slate-800/80">
                              <span className="text-slate-500 capitalize text-[8px] block font-bold truncate">{k.replace(/([A-Z])/g, ' $1')}</span>
                              <strong className="text-white text-[10px] block truncate">{String(v)}</strong>
                            </div>
                          ))}
                        </div>

                        {/* Missing info / compliance issues warnings */}
                        {ocrIssues.length > 0 && (
                          <div className="mt-1 pt-1.5 border-t border-slate-800 flex items-start gap-1.5 text-amber-300 text-[10px]">
                            <FaExclamationTriangle className="flex-shrink-0 mt-0.5" size={10} />
                            <span>{ocrIssues[0]}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            <div className="pt-2 flex gap-3">
              <button
                type="button"
                onClick={() => setActiveStep(1)}
                className="w-1/3 bg-[#12192e] border border-slate-700 text-slate-300 font-bold py-3.5 rounded-2xl text-xs"
              >
                &larr; Back
              </button>
              <button
                type="button"
                onClick={() => setActiveStep(3)}
                className="flex-1 bg-gradient-to-r from-cyan-400 to-blue-600 text-slate-950 font-black py-3.5 rounded-2xl text-xs uppercase tracking-wider shadow-lg hover:scale-[1.01] active:scale-95 transition-all cursor-pointer"
              >
                Proceed to Sign & Submit (Step 3/3) &rarr;
              </button>
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* STEP 3: DIGITAL SIGNATURE & SUBMISSION                                    */}
        {/* ========================================================================= */}
        {activeStep === 3 && (
          <div className="space-y-4 animate-fadeIn">
            {!submissionComplete ? (
              <>
                <div className="bg-[#0e1628] border border-cyan-500/30 rounded-3xl p-5 space-y-2 shadow-xl">
                  <div className="flex items-center gap-2 text-cyan-400">
                    <FaSignature size={16} />
                    <span className="text-[10px] font-black uppercase tracking-wider">STEP 3 &middot; DIGITAL SIGNATURE</span>
                  </div>
                  <h3 className="text-lg font-black text-white">Sign & finalize your application</h3>
                  <p className="text-xs text-slate-300 leading-relaxed">
                    Review your attached documents and sign digitally below to transmit your verified financing packet to your Sales Consultant.
                  </p>
                </div>

                {/* Document Summary Checklist */}
                <div className="bg-[#0f172a] border border-slate-750 rounded-3xl p-5 space-y-3 shadow-xl text-xs">
                  <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block border-b border-slate-800 pb-2">
                    SUBMITTED DOCUMENTATION CHECKLIST
                  </span>

                  <div className="space-y-2">
                    {["Driver's License (Front)", "Driver's License (Back)", "Proof of Income", "Insurance Card"].map((slot) => (
                      <div key={slot} className="flex justify-between items-center py-1 border-b border-slate-800/60">
                        <span className="text-slate-300">{slot}</span>
                        {uploadedDocs[slot] ? (
                          <span className="text-emerald-400 font-bold flex items-center gap-1 text-[11px]">
                            <FaCheck size={10} /> Attached (S3 &amp; OCR)
                          </span>
                        ) : (
                          <span className="text-amber-400 font-bold text-[11px]">
                            🟡 Pending
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Digital Signature Pad */}
                <div className="bg-[#0f172a] border border-cyan-500/30 rounded-3xl p-5 space-y-3 shadow-xl">
                  <div className="flex flex-wrap justify-between items-center gap-2 border-b border-slate-800 pb-2.5">
                    <span className="text-[10px] font-black text-cyan-400 uppercase tracking-widest flex items-center gap-1.5">
                      <FaPenNib size={11} /> CUSTOMER DIGITAL SIGNATURE
                    </span>
                    <div className="flex flex-wrap gap-1.5">
                      <button
                        type="button"
                        onClick={() => setSignatureType("DRAW")}
                        className={`px-2.5 py-1 rounded-lg text-[10px] font-bold border transition-colors ${
                          signatureType === "DRAW" ? 'bg-cyan-400 text-slate-950 border-cyan-400 shadow-sm' : 'bg-[#12192e] text-slate-400 border-slate-700 hover:text-white'
                        }`}
                      >
                        ✍️ Draw Signature
                      </button>
                      <button
                        type="button"
                        onClick={() => setSignatureType("UPLOAD")}
                        className={`px-2.5 py-1 rounded-lg text-[10px] font-bold border transition-colors ${
                          signatureType === "UPLOAD" ? 'bg-cyan-400 text-slate-950 border-cyan-400 shadow-sm' : 'bg-[#12192e] text-slate-400 border-slate-700 hover:text-white'
                        }`}
                      >
                        📸 Upload Sign Image
                      </button>
                      <button
                        type="button"
                        onClick={() => setSignatureType("TYPE")}
                        className={`px-2.5 py-1 rounded-lg text-[10px] font-bold border transition-colors ${
                          signatureType === "TYPE" ? 'bg-cyan-400 text-slate-950 border-cyan-400 shadow-sm' : 'bg-[#12192e] text-slate-400 border-slate-700 hover:text-white'
                        }`}
                      >
                        ⌨️ Type Name
                      </button>
                    </div>
                  </div>

                  {/* 1. DRAW SIGNATURE OPTION */}
                  {signatureType === "DRAW" && (
                    <div className="space-y-2">
                      <div className="relative border-2 border-dashed border-cyan-500/40 rounded-2xl bg-[#080d1a] h-36 w-full touch-none overflow-hidden">
                        <canvas
                          ref={signatureCanvasRef}
                          width={400}
                          height={144}
                          onMouseDown={startDrawing}
                          onMouseMove={draw}
                          onMouseUp={stopDrawing}
                          onTouchStart={startDrawing}
                          onTouchMove={draw}
                          onTouchEnd={stopDrawing}
                          className="w-full h-full cursor-crosshair"
                        />
                        {!drawnSignatureData && (
                          <div className="absolute inset-0 flex items-center justify-center pointer-events-none text-slate-500 text-xs font-serif italic">
                            ✍️ Draw your legal signature here with your finger
                          </div>
                        )}
                      </div>
                      <div className="flex justify-between items-center text-[10px]">
                        <span className="text-slate-400 font-medium">Draw legal signature using finger or stylus</span>
                        <button
                          type="button"
                          onClick={clearSignature}
                          className="text-slate-400 hover:text-rose-400 font-bold underline cursor-pointer"
                        >
                          Clear &amp; Redraw
                        </button>
                      </div>
                    </div>
                  )}

                  {/* 2. UPLOAD SIGNATURE IMAGE OPTION */}
                  {signatureType === "UPLOAD" && (
                    <div className="space-y-2">
                      {uploadedSignatureData ? (
                        <div className="space-y-2">
                          <div className="border-2 border-emerald-500/40 bg-white rounded-2xl p-3 h-36 w-full flex items-center justify-center overflow-hidden shadow-inner">
                            <img
                              src={uploadedSignatureData}
                              alt="Uploaded Signature"
                              className="max-h-full max-w-full object-contain"
                            />
                          </div>
                          <div className="flex justify-between items-center text-[10px]">
                            <span className="text-emerald-400 font-bold flex items-center gap-1">
                              <FaCheck size={10} /> Signature Image Attached
                            </span>
                            <div className="flex gap-2">
                              <label className="text-cyan-400 hover:text-cyan-300 font-bold underline cursor-pointer">
                                🔄 Replace Image
                                <input
                                  type="file"
                                  accept="image/*"
                                  onChange={handleSignatureImageUpload}
                                  className="hidden"
                                />
                              </label>
                              <button
                                type="button"
                                onClick={() => setUploadedSignatureData("")}
                                className="text-rose-400 hover:text-rose-300 font-bold underline cursor-pointer"
                              >
                                ✕ Clear
                              </button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <label className="border-2 border-dashed border-cyan-500/40 hover:border-cyan-400 rounded-2xl bg-[#080d1a] p-6 flex flex-col items-center justify-center cursor-pointer transition-all hover:bg-[#0c1426] text-center space-y-2 group">
                          <div className="w-10 h-10 rounded-full bg-cyan-500/10 text-cyan-400 flex items-center justify-center group-hover:scale-110 transition-transform">
                            <FaUpload size={16} />
                          </div>
                          <div className="space-y-0.5">
                            <span className="text-xs font-bold text-white block">Snap or Upload Signature Image</span>
                            <span className="text-[10px] text-slate-400 leading-tight block">
                              Sign on a sheet of paper and take a photo, or choose an image file (PNG, JPG)
                            </span>
                          </div>
                          <span className="text-[9px] bg-gradient-to-r from-cyan-400 to-blue-500 text-slate-950 font-black px-3.5 py-1.5 rounded-xl uppercase tracking-wider shadow-md mt-1">
                            📸 Camera / Browse Image
                          </span>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleSignatureImageUpload}
                            className="hidden"
                          />
                        </label>
                      )}
                    </div>
                  )}

                  {/* 3. TYPE NAME OPTION */}
                  {signatureType === "TYPE" && (
                    <div className="space-y-2">
                      <input
                        type="text"
                        value={typedSignature || `${customerInfo.firstName} ${customerInfo.lastName}`}
                        onChange={(e) => setTypedSignature(e.target.value)}
                        placeholder="Type full legal name"
                        className="w-full bg-[#141d33] border border-cyan-500/40 focus:border-cyan-400 rounded-2xl px-4 py-3.5 text-cyan-300 font-serif italic text-lg outline-none"
                      />
                      <span className="text-[10px] text-slate-400 block">
                        Will be formatted legally under E-SIGN Act standards.
                      </span>
                    </div>
                  )}

                  {/* Credit Authorization Checkbox */}
                  <label className="flex items-start gap-3 cursor-pointer pt-2 border-t border-slate-800">
                    <input
                      type="checkbox"
                      checked={creditAuthAgreed}
                      onChange={(e) => setCreditAuthAgreed(e.target.checked)}
                      className="w-5 h-5 rounded-lg text-cyan-400 accent-cyan-400 mt-0.5 cursor-pointer flex-shrink-0"
                    />
                    <div className="space-y-0.5 text-xs">
                      <span className="font-black text-white block">Credit Authorization &amp; Accuracy Certification</span>
                      <p className="text-[10px] text-slate-400 leading-snug">
                        I certify that all uploaded documentation and income information are accurate, and authorize the dealership to perform credit inquiry underwriting.
                      </p>
                    </div>
                  </label>
                </div>

                <div className="pt-2 flex gap-3">
                  <button
                    type="button"
                    onClick={() => setActiveStep(2)}
                    className="w-1/3 bg-[#12192e] border border-slate-700 text-slate-300 font-bold py-4 rounded-2xl text-xs"
                  >
                    &larr; Back
                  </button>
                  <button
                    type="button"
                    disabled={isSubmitting}
                    onClick={handleFinalSubmit}
                    className="flex-1 bg-gradient-to-r from-emerald-400 via-teal-400 to-cyan-500 text-slate-950 font-black py-4 rounded-2xl text-xs uppercase tracking-wider shadow-2xl hover:scale-[1.01] active:scale-95 transition-all cursor-pointer flex items-center justify-center gap-2"
                  >
                    {isSubmitting ? (
                      <span className="w-4 h-4 border-2 border-slate-950 border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        <FaPaperPlane size={13} /> Submit Signed Documentation &rarr;
                      </>
                    )}
                  </button>
                </div>
              </>
            ) : (
              /* Success / Transmitted State */
              <div className="bg-gradient-to-br from-[#0c182c] via-[#0e1628] to-[#070e1e] border-2 border-emerald-500/50 rounded-3xl p-6 text-center space-y-4 shadow-2xl animate-scaleIn">
                <div className="w-16 h-16 rounded-full bg-emerald-500/20 border-2 border-emerald-400 text-emerald-400 flex items-center justify-center mx-auto text-2xl shadow-[0_0_30px_rgba(16,185,129,0.3)]">
                  <FaCheck />
                </div>
                <div className="space-y-1.5">
                  <span className="text-[10px] font-black text-emerald-400 uppercase tracking-widest block">
                    APPLICATION TRANSMITTED LIVE
                  </span>
                  <h3 className="text-xl font-black text-white">Documentation Signed &amp; Submitted!</h3>
                  <p className="text-xs text-slate-300 leading-relaxed max-w-sm mx-auto">
                    Your Sales Consultant has received your complete deal package, S3 documents, OCR extractions, and signature on their desk workstation.
                  </p>
                </div>

                <div className="bg-[#12192e] border border-slate-750 p-4 rounded-2xl text-left space-y-2 text-xs">
                  <div className="flex justify-between items-center text-[11px]">
                    <span className="text-slate-400">Deal Status:</span>
                    <span className="text-emerald-400 font-black flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" /> {deal?.status || "IN_PROGRESS / REVIEW"}
                    </span>
                  </div>
                  <div className="flex justify-between items-center text-[11px]">
                    <span className="text-slate-400">Deal Reference:</span>
                    <span className="font-bold text-white">#{deal?.dealNumber || "PHS-00124"}</span>
                  </div>
                  <div className="flex justify-between items-center text-[11px]">
                    <span className="text-slate-400">S3 Documents:</span>
                    <span className="font-bold text-cyan-400">{Object.keys(uploadedDocs).length} Uploaded &amp; OCR Analyzed</span>
                  </div>
                </div>

                <div className="pt-2 space-y-2">
                  <button
                    type="button"
                    onClick={() => setShowPrintReportModal(true)}
                    className="w-full bg-gradient-to-r from-emerald-400 via-teal-400 to-cyan-400 text-slate-950 font-black py-3.5 rounded-2xl text-xs uppercase tracking-wider shadow-2xl hover:scale-[1.01] active:scale-95 transition-all cursor-pointer flex items-center justify-center gap-2"
                  >
                    <FaPrint size={14} /> View &amp; Print Official Buyer's Order (PDF)
                  </button>

                  <button
                    onClick={() => setActiveStep(2)}
                    className="text-xs text-cyan-400 hover:text-cyan-300 font-bold underline cursor-pointer block mx-auto pt-1"
                  >
                    View or add additional documentation
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

      </main>

      {/* Floating Bottom Live Sync Status */}
      <footer className="fixed bottom-0 left-0 right-0 z-40 bg-[#080d1a]/95 backdrop-blur-xl border-t border-slate-800 p-3 max-w-lg mx-auto w-full flex items-center justify-between text-[11px]">
        <div className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-ping" />
          <span className="text-slate-300 font-medium">Session: <strong className="text-white">{customerInfo.firstName} {customerInfo.lastName}</strong></span>
        </div>
        <button
          type="button"
          onClick={() => {
            fetchDeal(true);
            toast.info("Refreshed live deal status.");
          }}
          className="flex items-center gap-1.5 text-cyan-400 font-bold bg-cyan-500/10 px-2.5 py-1 rounded-lg border border-cyan-500/30 cursor-pointer"
        >
          <FaSyncAlt size={10} /> Sync Now
        </button>
      </footer>
      {/* EDIT / REVIEW OCR FIELDS MODAL */}
      {editingOcrSlot && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4 animate-fadeIn">
          <div className="bg-[#0e1628] border-2 border-cyan-500/40 rounded-3xl p-5 max-w-md w-full max-h-[85vh] flex flex-col shadow-2xl space-y-4">
            <div className="flex justify-between items-center border-b border-slate-800 pb-3 flex-shrink-0">
              <div>
                <span className="text-[9px] font-black text-cyan-400 uppercase tracking-widest block">AI OCR FIELD INSPECTOR</span>
                <h3 className="text-sm font-black text-white">{editingOcrSlot}</h3>
              </div>
              <button
                onClick={() => setEditingOcrSlot(null)}
                className="text-slate-400 hover:text-white text-xs cursor-pointer p-1"
              >
                ✕
              </button>
            </div>

            <div className="flex-1 overflow-y-auto space-y-3 pr-1 text-xs">
              <p className="text-[11px] text-slate-300">
                Review or correct any extracted details before submitting to the salesperson:
              </p>
              {Object.entries(editOcrFields).map(([key, val]) => (
                <div key={key} className="space-y-1">
                  <label className="text-[10px] font-bold text-slate-400 capitalize block">
                    {key.replace(/([A-Z])/g, ' $1')}
                  </label>
                  <input
                    type="text"
                    value={val}
                    onChange={(e) => setEditOcrFields({ ...editOcrFields, [key]: e.target.value })}
                    className="w-full bg-[#060a14] border border-slate-750 focus:border-cyan-400 rounded-xl px-3 py-2 text-xs text-white outline-none"
                  />
                </div>
              ))}
            </div>

            <div className="flex gap-2.5 pt-2 border-t border-slate-800 flex-shrink-0">
              <button
                type="button"
                onClick={() => setEditingOcrSlot(null)}
                className="w-1/3 bg-[#141d33] text-slate-300 font-bold py-2.5 rounded-xl text-xs hover:bg-[#1a2642]"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleSaveOcrFields(editingOcrSlot, editOcrFields)}
                className="flex-1 bg-gradient-to-r from-cyan-400 to-blue-500 text-slate-950 font-black py-2.5 rounded-xl text-xs uppercase tracking-wider hover:brightness-110 shadow-lg"
              >
                Save &amp; Verify &rarr;
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Official Automotive Buyer's Order / Deal Sheet Modal */}
      <DealPrintReport
        isOpen={showPrintReportModal}
        onClose={() => setShowPrintReportModal(false)}
        deal={deal}
        customerInfo={customerInfo}
        uploadedDocs={uploadedDocs}
        paymentDetails={{
          downPayment: customerInfo.downPayment || deal?.finance?.downPayment || 0,
          customerRebate: deal?.finance?.rebate || 0,
          tradeEquity: deal?.trade?.equity || 0,
          apr: deal?.finance?.apr || 5.99,
          term: deal?.finance?.term || 72,
          paymentPreference: deal?.finance?.paymentType || "FINANCE"
        }}
      />
    </div>
  );
};

export default CustomerMobileKiosk;
